import { TChatbotVisualization, TRESPONSE_AI_ANALYTICS_QUESTION } from '@monorepo/types';
import { AI_ANALYTICS_CHART_MODEL } from '../../../../charting';
import { AI_ANALYTICS_TABLE_MODEL } from '../../../../table/models/create';
import { ChatUiModelModifiers } from '../../chat-ui.modifier';
import { TChatUiModel, TChatUiModelFeedback, TChatUiTableAndChartResponseData } from '../../chat-ui.types';

const DEFAULT_ERROR_RESPONSE: TChatUiModel<TRESPONSE_AI_ANALYTICS_QUESTION>['chats'][number] = {
  type: 'RESPONSE',
  responseType: 'ERROR',
  errorText: 'chat_ui_model.DefaultErrorMsg',
  rawData: {
    status: 'error',
    natural_language_summary: '',
    original_query: '',
    validation_errors: [],
  },
};

export const AI_ANALYTICS_CHAT_UI_MODEL = {
  createIdentityModel: function (): TChatUiModel<TRESPONSE_AI_ANALYTICS_QUESTION> {
    return ChatUiModelModifiers.createIdentityModel();
  },

  addNewResponse: function (
    prevModel: TChatUiModel<TRESPONSE_AI_ANALYTICS_QUESTION>,
    response: TRESPONSE_AI_ANALYTICS_QUESTION | null,
    isMobileScreen: boolean,
  ): TChatUiModel<TRESPONSE_AI_ANALYTICS_QUESTION> {
    try {
      if (!response) return ChatUiModelModifiers.addNewChat(prevModel, DEFAULT_ERROR_RESPONSE);

      switch (response.status) {
        case 'failed':
        case 'error':
          return ChatUiModelModifiers.addNewChat(prevModel, {
            type: 'RESPONSE',
            responseType: 'ERROR',
            errorText:
              response.error_message ??
              response.natural_language_summary ??
              response.validation_errors.reduce((err, acc) => acc + err, ''),
            rawData: response,
          });

        case 'success':
          return ChatUiModelModifiers.addNewChat(prevModel, {
            type: 'RESPONSE',
            responseType: 'TABLE_AND_CHART',
            data: {
              tab: 'TABLE',
              highChartsConfig: getChartConfig(response.visualizations[0], isMobileScreen),
              tableModel: AI_ANALYTICS_TABLE_MODEL.createModel(response.headers, response.query_results),
              dataSummaryMessage:
                response.natural_language_summary ??
                `Found ${response.query_results.length} result(s) from your query:`,
              answerExplanation: response.answer_explanation,
              insightsInfo: { status: 'IDLE' },
              showDownloadBtn: !isMobileScreen,
              showViewBtns: isMobileScreen,
            },
            showFeedbackUi: true,
            rawData: response,
          });

        case 'follow_up_needed':
        case 'clarification_needed':
          return ChatUiModelModifiers.addNewChat(prevModel, {
            type: 'RESPONSE',
            responseType: 'TEXT',
            displayText: `${response.natural_language_summary ?? ''} \n ${response.clarification_message ?? ''}`,
            rawData: response,
          });

        default:
          return ChatUiModelModifiers.addNewChat(prevModel, {
            type: 'RESPONSE',
            responseType: 'ERROR',
            errorText: "Sorry! We couldn't follow up on this.",
            rawData: response,
          });
      }
    } catch (_e) {
      return ChatUiModelModifiers.addNewChat(prevModel, DEFAULT_ERROR_RESPONSE);
    }
  },

  upsertSeparator: function (
    prevModel: TChatUiModel<TRESPONSE_AI_ANALYTICS_QUESTION>,
    text: string,
  ): TChatUiModel<TRESPONSE_AI_ANALYTICS_QUESTION> {
    return ChatUiModelModifiers.upsertTrailingSeparator(prevModel, text);
  },

  setFeedbackSubmitByIndex: function (
    prevModel: TChatUiModel<TRESPONSE_AI_ANALYTICS_QUESTION>,
    index: number,
    feedback: TChatUiModelFeedback,
  ): TChatUiModel<TRESPONSE_AI_ANALYTICS_QUESTION> {
    return ChatUiModelModifiers.submitFeedbackForResponseByIndex(prevModel, index, feedback);
  },

  updateInsigtsInfoByIndex: function (
    prevModel: TChatUiModel<TRESPONSE_AI_ANALYTICS_QUESTION>,
    index: number,
    insightsInfo: TChatUiTableAndChartResponseData['insightsInfo'],
  ): TChatUiModel<TRESPONSE_AI_ANALYTICS_QUESTION> {
    return ChatUiModelModifiers.updateInsightsInfoForTableChartResponseByIndex(prevModel, index, insightsInfo);
  },
};

function getChartConfig(visualization: TChatbotVisualization, isMobileScreen: boolean): Highcharts.Options | undefined {
  try {
    switch (visualization.type) {
      case 'bar':
      case 'line':
      case 'scatter':
      case 'histogram':
      case 'pie':
        return AI_ANALYTICS_CHART_MODEL.createModel(visualization, isMobileScreen);

      default:
        return undefined;
    }
  } catch (error) {
    return undefined;
  }
}
